/* ============================================================================
 * InstallPrompt — post-login "Add to home screen" nudge.
 *
 * Behavior:
 *   • Only shown to authenticated users (mounted inside DashboardLayout).
 *   • Skipped entirely when already running standalone (isStandalone()).
 *   • On Chromium-family browsers: listens for beforeinstallprompt and
 *     offers a native Install button.
 *   • On iOS Safari: no native prompt exists, so shows short manual
 *     instructions (Share → Add to Home Screen).
 *   • Dismissal is remembered (localStorage `kocore.pwa.installDismissed`).
 *   • Auto-hides after successful install (via `appinstalled` event).
 *
 * Visual: bottom-right floating card on desktop, full-width bottom sheet on
 * mobile. Dark + gold brand palette. Keyboard-accessible dismiss.
 * ========================================================================== */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Share, Smartphone, X, PlusSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  PWA_EVENTS,
  getDeferredPrompt,
  isIosSafari,
  isStandalone,
  onPwaEvent,
  triggerInstall,
} from '@/lib/pwaRegister';

const DISMISS_KEY = 'kocore.pwa.installDismissed';
const SHOW_DELAY_MS = 4000; // small delay after login so it doesn't collide with first-paint KPI fetches

const hasDismissed = () => {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const markDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch (_) {
    /* noop */
  }
};

export default function InstallPrompt() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [canPrompt, setCanPrompt] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    // Never show once installed or if user previously dismissed.
    if (isStandalone() || hasDismissed()) return undefined;

    // iOS Safari can't native-prompt — fall back to instructions,
    // but only on actual phones (skip iPad desktop-class Safari).
    if (isIosSafari()) {
      setIosMode(true);
      const t1 = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
      return () => clearTimeout(t1);
    }

    // If the browser already fired beforeinstallprompt before mount,
    // a deferred prompt will be stored on the module.
    if (getDeferredPrompt()) {
      setCanPrompt(true);
      const t1 = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
      return () => clearTimeout(t1);
    }

    // Otherwise listen for the event.
    const offAvail = onPwaEvent(PWA_EVENTS.INSTALL_AVAILABLE, () => {
      setCanPrompt(true);
      setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    });
    const offInstalled = onPwaEvent(PWA_EVENTS.APP_INSTALLED, () => {
      setVisible(false);
      markDismissed();
    });
    return () => {
      offAvail();
      offInstalled();
    };
  }, []);

  const handleInstall = async () => {
    const outcome = await triggerInstall();
    if (outcome === 'accepted') {
      setVisible(false);
      markDismissed();
    } else if (outcome === 'dismissed') {
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    markDismissed();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="install-prompt-title"
      className={cn(
        'fixed z-[60] transition-all duration-200',
        // Mobile: anchored to the bottom edge with a safe-area inset
        'inset-x-3 bottom-3',
        // Desktop: floating bottom-right
        'sm:inset-auto sm:right-6 sm:bottom-6 sm:w-[380px]'
      )}
      data-testid="install-prompt"
    >
      <div
        className={cn(
          'rounded-none border border-[hsl(var(--hairline))] bg-[hsl(var(--card))]/95 backdrop-blur',
          'shadow-[0_12px_40px_-12px_rgba(0,0,0,0.7)]'
        )}
      >
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-[hsl(var(--accent))]/15 flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-4 w-4 text-[hsl(var(--accent))]" />
            </div>
            <div className="flex-1 min-w-0">
              <div
                id="install-prompt-title"
                className="text-sm font-medium text-foreground"
              >
                {t('pwa.title')}
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {iosMode ? t('pwa.ios_body') : t('pwa.body')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label={t('common.close')}
              className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] flex-shrink-0"
              data-testid="install-prompt-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {iosMode ? (
            <div className="mt-3 pt-3 border-t border-[hsl(var(--hairline))] space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Share className="h-3.5 w-3.5 text-[hsl(var(--accent))]" aria-hidden />
                <span>{t('pwa.ios_step1')}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <PlusSquare className="h-3.5 w-3.5 text-[hsl(var(--accent))]" aria-hidden />
                <span>{t('pwa.ios_step2')}</span>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={handleDismiss}
                className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
                data-testid="install-prompt-later"
              >
                {t('pwa.later')}
              </Button>
              <Button
                onClick={handleInstall}
                disabled={!canPrompt}
                className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                data-testid="install-prompt-install"
              >
                <Download className="h-4 w-4 mr-2" />
                {t('pwa.install')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
